/**
 * ArticleSelectorModal Component
 *
 * Modal for selecting articles to add as clips to an episode.
 * Split-panel layout:
 * - Left panel: Article browser with search and scrollable list
 * - Right panel: Selected articles with drag-to-reorder functionality
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { customToast } from '@/components/ui/custom-toast';
import { Reorder, AnimatePresence, motion } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'shell/Dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  Search,
  Check,
  AlertCircle,
  Newspaper,
  GripVertical,
  X,
  FileText,
  RefreshCw,
} from 'lucide-react';

import { ArticleService, ArticleWithFeed, FeedInfo } from '@/services/ArticleService';
import { PodcastEpisodeService } from '@/services/PodcastEpisodeService';
import { ArticleForClip, Clip } from '@/types/clip.types';
import { useUserCompanies } from '@/contexts/UserCompaniesContext';
import { format } from 'date-fns';
import { FeatureBanner } from './FeatureBanner';

interface ArticleSelectorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  episodeId: string;
  companyId: string;
  onArticlesAdded?: () => void;
  existingClipCount?: number;
  existingClips?: Clip[];
}

const DEFAULT_MAX_ARTICLES = 5;

// Skeleton component for loading state
const ArticleSkeleton: React.FC = () => (
  <div className="w-full p-3 border-l-2 border-l-transparent">
    <div className="flex items-start gap-3 animate-pulse">
      {/* Checkbox placeholder */}
      <div className="flex-shrink-0 w-5 h-5 rounded border-2 border-muted bg-muted mt-0.5" />
      {/* Thumbnail placeholder */}
      <div className="flex-shrink-0 w-16 h-16 rounded-md bg-muted" />
      {/* Content area */}
      <div className="flex-1 min-w-0">
        {/* Title - two lines */}
        <div className="h-4 bg-muted rounded w-3/4 mb-1" />
        <div className="h-4 bg-muted rounded w-1/2 mb-2" />
        {/* Excerpt - two lines */}
        <div className="h-3 bg-muted rounded w-full mb-1" />
        <div className="h-3 bg-muted rounded w-2/3 mb-2" />
        {/* Meta row - badge + date */}
        <div className="flex items-center gap-2">
          <div className="h-5 bg-muted rounded w-12" />
          <div className="h-3 bg-muted rounded w-20" />
        </div>
      </div>
    </div>
  </div>
);

// Helper to extract first N sentences from text
const getFirstSentences = (text: string, count: number): string => {
  if (!text) return '';
  // Match sentences ending with . ! or ? followed by space or end of string
  const sentences = text.match(/[^.!?]*[.!?]+/g) || [];
  return sentences.slice(0, count).join(' ').trim();
};

export const ArticleSelectorModal: React.FC<ArticleSelectorModalProps> = ({
  open,
  onOpenChange,
  episodeId,
  companyId,
  onArticlesAdded,
  existingClipCount = 0,
  existingClips = [],
}) => {
  const { userCompanies, getCompanyName, getCompanyColor } = useUserCompanies();
  const isMultiCompany = userCompanies.length > 1;
  const [articles, setArticles] = useState<ArticleWithFeed[]>([]);
  const [availableFeeds, setAvailableFeeds] = useState<FeedInfo[]>([]);
  const [enabledFeedIds, setEnabledFeedIds] = useState<Set<string>>(new Set());
  const [currentCompanyId, setCurrentCompanyId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedArticles, setSelectedArticles] = useState<ArticleWithFeed[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [badgePop, setBadgePop] = useState(false);
  const [maxArticles, setMaxArticles] = useState(DEFAULT_MAX_ARTICLES);
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [initialHeight, setInitialHeight] = useState<number | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const prevCountRef = useRef(0);
  const articleListRef = useRef<HTMLDivElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dialogContentRef = useRef<HTMLDivElement>(null);

  // Calculate remaining slots
  const remainingSlots = maxArticles - existingClipCount;
  const maxSelectable = Math.max(0, remainingSlots);

  // Create a set of article URLs that are already in the episode
  const existingArticleUrls = useMemo(() => {
    return new Set(existingClips.map((clip) => clip.articleUrl));
  }, [existingClips]);

  // Fetch articles when modal opens
  useEffect(() => {
    if (open) {
      loadArticles();
      setSelectedArticles([]);
      setSearchTerm('');
      setSearchExpanded(false);
      setInitialHeight(null); // Reset so we capture new height
      setShowCelebration(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Capture initial height once content loads
  useEffect(() => {
    if (!loading && initialHeight === null && dialogContentRef.current) {
      setInitialHeight(dialogContentRef.current.offsetHeight);
    }
  }, [loading, initialHeight]);

  // Focus search input when expanded
  useEffect(() => {
    if (searchExpanded && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchExpanded]);

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (searchContainerRef.current && !searchContainerRef.current.contains(e.target as Node)) {
        setSearchExpanded(false);
      }
    };
    if (searchExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [searchExpanded]);

  const loadArticles = async (refresh?: boolean) => {
    setLoading(true);
    setError(null);
    try {
      // Fetch articles for the episode's company (includes all feeds in the group)
      const result = await ArticleService.fetchArticles(companyId, refresh);

      // Set max articles from response
      setMaxArticles(result.maxArticlesPerEpisode);

      // Set articles and feeds
      setArticles(result.articles);
      setAvailableFeeds(result.feeds);
      setCurrentCompanyId(result.currentCompanyId);

      // Default filter: enable only current company's feeds
      const currentCompanyFeedIds = new Set(
        result.feeds
          .filter(feed => feed.companyId === result.currentCompanyId)
          .map(feed => feed.id)
      );
      setEnabledFeedIds(currentCompanyFeedIds);
    } catch (err) {
      console.error('Failed to fetch articles:', err);
      setError(err instanceof Error ? err.message : 'Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  // Refresh articles bypassing cache
  const handleRefresh = () => {
    loadArticles(true);
  };

  // Filter articles based on enabled feeds and search
  const filteredArticles = useMemo(() => {
    // First filter by enabled feeds
    let filtered = articles.filter(article => enabledFeedIds.has(article.feedId));

    // Then filter by search term
    if (searchTerm) {
      const lowerSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (article) =>
          article.title.toLowerCase().includes(lowerSearch) ||
          article.excerpt.toLowerCase().includes(lowerSearch) ||
          article.author.toLowerCase().includes(lowerSearch) ||
          (article.sourcePlatform || '').toLowerCase().includes(lowerSearch)
      );
    }

    return filtered;
  }, [articles, enabledFeedIds, searchTerm]);

  // Toggle feed filter
  const toggleFeed = (feedId: string) => {
    setEnabledFeedIds(prev => {
      const next = new Set(prev);
      if (next.has(feedId)) {
        next.delete(feedId);
      } else {
        next.add(feedId);
      }
      return next;
    });
  };

  // Check if a feed belongs to the current company
  const isCurrentCompanyFeed = (feed: FeedInfo) => feed.companyId === currentCompanyId;

  // Sort feeds: current company first, then alphabetically by label
  const sortedFeeds = useMemo(() => {
    const currentCompanyFeeds = availableFeeds
      .filter(feed => feed.companyId === currentCompanyId)
      .sort((a, b) => a.label.localeCompare(b.label));
    const otherFeeds = availableFeeds
      .filter(feed => feed.companyId !== currentCompanyId)
      .sort((a, b) => a.label.localeCompare(b.label));
    return [...currentCompanyFeeds, ...otherFeeds];
  }, [availableFeeds, currentCompanyId]);

  // Toggle article selection
  const toggleArticle = (article: ArticleWithFeed, rowElement?: HTMLElement) => {
    const isSelected = selectedArticles.some((a) => a.id === article.id);

    if (isSelected) {
      setSelectedArticles((prev) => prev.filter((a) => a.id !== article.id));
    } else {
      if (selectedArticles.length >= maxSelectable) {
        customToast.error('Maximum articles reached', {
          description: `You can only add ${maxSelectable} more article${maxSelectable !== 1 ? 's' : ''} to this episode`,
        });
        return;
      }
      setSelectedArticles((prev) => [...prev, article]);

      // Scroll up by the row height to show the next article
      if (rowElement && articleListRef.current) {
        const rowHeight = rowElement.offsetHeight;
        articleListRef.current.scrollBy({ top: rowHeight, behavior: 'smooth' });
      }
    }
  };

  // Handle submit
  const handleSubmit = async () => {
    if (selectedArticles.length === 0) return;

    setSubmitting(true);
    try {
      // Convert ArticleData to ArticleForClip format
      const articlesForClip: ArticleForClip[] = selectedArticles.map((article) => ({
        id: article.id,
        title: article.title,
        url: article.articleUrl,
        content: article.content,
        excerpt: article.excerpt,
        author: article.author,
        publishedAt: article.publishDate?.toISOString(),
        imageUrl: article.imageUrl,
        feedLabel: typeof article.sourcePlatform === 'string' ? article.sourcePlatform : undefined,
      }));

      // Add clips to episode via API
      await PodcastEpisodeService.addClipsToEpisode(episodeId, articlesForClip, companyId);

      customToast.success(`Added ${selectedArticles.length} article${selectedArticles.length !== 1 ? 's' : ''} to episode`);
      onArticlesAdded?.();
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to add articles:', err);
      customToast.error('Failed to add articles', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Check if limit reached
  const isLimitReached = selectedArticles.length >= maxSelectable;

  // Trigger badge pop animation and celebration toast when count changes
  useEffect(() => {
    const prevCount = prevCountRef.current;
    const currentCount = selectedArticles.length;

    if (currentCount !== prevCount) {
      // Badge pop animation
      setBadgePop(true);
      const popTimer = setTimeout(() => setBadgePop(false), 300);

      // Celebration toast when hitting the limit
      if (currentCount === maxSelectable && maxSelectable > 0 && prevCount === maxSelectable - 1) {
        setShowCelebration(true);
        const celebrationTimer = setTimeout(() => setShowCelebration(false), 4000);
        prevCountRef.current = currentCount;
        return () => {
          clearTimeout(popTimer);
          clearTimeout(celebrationTimer);
        };
      }

      prevCountRef.current = currentCount;
      return () => clearTimeout(popTimer);
    }
  }, [selectedArticles.length, maxSelectable]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        ref={dialogContentRef}
        className="max-w-[1300px] min-h-[400px] max-h-[800px] flex flex-col bg-podcast"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isMultiCompany && (
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium text-white flex-shrink-0"
                style={{ backgroundColor: getCompanyColor(companyId) || '#4A90E2' }}
                title={getCompanyName(companyId)}
              >
                {getCompanyName(companyId).substring(0, 2).toUpperCase()}
              </div>
            )}
            Add Articles
            {isMultiCompany && (
              <span className="text-sm font-normal text-muted-foreground">
                from {getCompanyName(companyId)}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Select up to {maxSelectable} articles for your episode
          </DialogDescription>
        </DialogHeader>

        {/* Feature announcement banner */}
        <FeatureBanner contextTrigger="episode-composer:article-picker" />

        {/* Panel styles */}
        <style>{`
          .article-panel {
            flex: 1;
            display: flex;
            flex-direction: column;
            background: var(--pm-bg-card);
            border: 1px solid var(--pm-cyan-border-faint);
            border-radius: 12px;
            overflow: hidden;
            min-width: 0;
          }
          .panel-header {
            padding: 14px 16px;
            background: var(--pm-cyan-bg);
            border-bottom: 1px solid var(--pm-cyan-border-faint);
            display: flex;
            align-items: center;
            justify-content: space-between;
            flex-shrink: 0;
          }
          .panel-title {
            font-size: 13px;
            font-weight: 600;
            color: var(--pm-text-muted);
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
          .panel-actions {
            display: flex;
            align-items: center;
            gap: 4px;
          }
          .icon-btn {
            background: none;
            border: none;
            color: var(--pm-text-faint);
            cursor: pointer;
            padding: 6px;
            border-radius: 6px;
            display: flex;
            align-items: center;
            justify-content: center;
            transition: all 0.2s;
          }
          .icon-btn:hover {
            background: var(--pm-bg-card-alt);
            color: var(--pm-cyan);
          }
          .icon-btn.active {
            background: var(--pm-bg-card-alt);
            color: var(--pm-cyan);
          }
          .search-container {
            position: relative;
          }
          .search-expanded {
            position: absolute;
            right: 0;
            top: 100%;
            margin-top: 8px;
            background: var(--pm-bg-card);
            border: 1px solid var(--pm-cyan-border-faint);
            border-radius: 8px;
            padding: 8px;
            z-index: 10;
            display: none;
          }
          .search-expanded.active {
            display: block;
          }
          .search-input {
            background: var(--pm-bg-base);
            border: 1px solid var(--pm-cyan-border-faint);
            border-radius: 6px;
            padding: 8px 12px;
            color: var(--pm-text-primary);
            font-size: 14px;
            width: 240px;
            font-family: inherit;
          }
          .search-input:focus {
            outline: none;
            border-color: var(--pm-cyan);
          }
          .search-input::placeholder {
            color: var(--pm-text-faint);
          }
          .panel-content {
            flex: 1;
            overflow-y: auto;
            padding: 8px;
          }
          .count-badge {
            background: var(--pm-cyan);
            color: var(--pm-bg-base);
            font-size: 12px;
            font-weight: 600;
            padding: 4px 10px;
            border-radius: 12px;
          }
          .count-badge.limit-reached {
            background: var(--pm-green-dim);
          }
          .celebration-toast {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) scale(0.8);
            background: var(--pm-green-dim);
            border: 2px solid var(--pm-green-dim);
            border-radius: 16px;
            padding: 32px 48px;
            display: flex;
            align-items: center;
            gap: 20px;
            opacity: 0;
            pointer-events: none;
            transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
            box-shadow: 0 16px 64px var(--pm-shadow-strong), 0 0 0 1px var(--pm-surface-4) inset;
            z-index: 9999;
          }
          .celebration-toast.show {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
            pointer-events: auto;
          }
          .toast-check-circle {
            width: 56px;
            height: 56px;
            background: var(--pm-bg-base);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transform: scale(0);
            transition: transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s;
          }
          .celebration-toast.show .toast-check-circle {
            transform: scale(1);
          }
          .toast-check-icon {
            width: 28px;
            height: 28px;
            stroke: var(--pm-green-dim);
            stroke-width: 3;
            fill: none;
            stroke-linecap: round;
            stroke-linejoin: round;
            stroke-dasharray: 24;
            stroke-dashoffset: 24;
            transition: stroke-dashoffset 0.3s ease 0.4s;
          }
          .celebration-toast.show .toast-check-icon {
            stroke-dashoffset: 0;
          }
          .toast-content {
            display: flex;
            flex-direction: column;
            gap: 4px;
          }
          .toast-title {
            font-size: 20px;
            font-weight: 700;
            color: var(--pm-bg-base);
          }
          .toast-subtitle {
            font-size: 15px;
            color: var(--pm-text-muted);
          }
        `}</style>

        {/* Split panel container */}
        <div className="flex gap-4 flex-1 min-h-0">
          {/* Left Panel - Article Browser */}
          <div className="article-panel">
            {/* Panel Header */}
            <div className="panel-header">
              <span className="panel-title">Available Articles</span>
              <div className="panel-actions">
                <div className="search-container" ref={searchContainerRef}>
                  <button
                    className={`icon-btn ${searchExpanded ? 'active' : ''}`}
                    title="Search"
                    onClick={() => setSearchExpanded(!searchExpanded)}
                  >
                    <Search className="h-4 w-4" />
                  </button>
                  <div className={`search-expanded ${searchExpanded ? 'active' : ''}`}>
                    <div className="relative">
                      <input
                        ref={searchInputRef}
                        type="text"
                        className="search-input"
                        placeholder="Search articles..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ paddingRight: searchTerm ? '32px' : '12px' }}
                      />
                      {searchTerm && (
                        <button
                          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-[var(--pm-bg-card-alt)] rounded text-[var(--pm-text-faint)] hover:text-[var(--pm-cyan)] transition-colors"
                          onClick={() => {
                            setSearchTerm('');
                            setSearchExpanded(false);
                          }}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Feed Filter Bar */}
            {sortedFeeds.length >= 1 && (
              <div className="px-3 py-2 border-b border-[var(--pm-cyan-border-faint)] bg-[var(--pm-cyan-bg)]">
                <div className="flex flex-wrap gap-1.5">
                  {sortedFeeds.map(feed => {
                    const isEnabled = enabledFeedIds.has(feed.id);
                    const isOwnFeed = isCurrentCompanyFeed(feed);
                    return (
                      <button
                        key={feed.id}
                        onClick={() => toggleFeed(feed.id)}
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all border ${
                          isEnabled
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-transparent text-muted-foreground border-border hover:border-primary/50 hover:text-foreground'
                        }`}
                        title={isOwnFeed ? 'Your company\'s feed' : `From another company in your group`}
                      >
                        {isEnabled && <Check className="h-3 w-3" />}
                        {feed.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  {enabledFeedIds.size === 0
                    ? 'No feeds selected'
                    : `${filteredArticles.length} articles from ${enabledFeedIds.size} feed${enabledFeedIds.size !== 1 ? 's' : ''}`}
                </p>
              </div>
            )}

            {/* Panel Content - Articles list */}
            <div ref={articleListRef} className="panel-content">
              {loading ? (
                <div className="divide-y">
                  {Array.from({ length: 20 }).map((_, i) => (
                    <ArticleSkeleton key={i} />
                  ))}
                </div>
              ) : error ? (
                <div className="flex flex-col items-center justify-center py-12 text-center px-4">
                  <AlertCircle className="h-8 w-8 text-destructive mb-2" />
                  <p className="text-sm text-destructive">{error}</p>
                  <Button variant="outline" size="sm" onClick={handleRefresh} className="mt-2">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh Feeds
                  </Button>
                </div>
              ) : filteredArticles.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center px-4">
                  <p className="text-muted-foreground">No articles found</p>
                </div>
              ) : (
                <div className="divide-y">
                  {filteredArticles.map((article) => {
                    const isSelected = selectedArticles.some((a) => a.id === article.id);
                    const isAlreadyAdded = existingArticleUrls.has(article.articleUrl);
                    const isDisabled = isAlreadyAdded || (!isSelected && isLimitReached);

                    return (
                      <button
                        key={article.id}
                        data-testid="article-item"
                        onClick={(e) => toggleArticle(article, e.currentTarget)}
                        disabled={isDisabled}
                        className={`w-full text-left p-3 transition-colors border-l-2 ${
                          isAlreadyAdded ? 'bg-muted/30 border-l-muted-foreground/50' :
                          isSelected ? 'bg-accent border-l-primary' : 'border-l-transparent hover:bg-muted/50'
                        } ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5 ${
                              isAlreadyAdded
                                ? 'bg-muted-foreground/50 border-muted-foreground/50 text-muted'
                                : isSelected
                                ? 'bg-primary border-primary text-primary-foreground'
                                : 'border-muted-foreground'
                            }`}
                          >
                            {(isSelected || isAlreadyAdded) && <Check className="h-3 w-3" />}
                          </div>
                          <div className="flex-shrink-0 w-16 h-16 rounded-md overflow-hidden bg-muted">
                            {article.imageUrl ? (
                              <img
                                src={article.imageUrl}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Newspaper className="h-6 w-6 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm line-clamp-2">
                              {article.title}
                            </h4>
                            {article.excerpt && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                {getFirstSentences(article.excerpt, 2)}
                              </p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="secondary" className="text-xs px-1.5 py-0">
                                {article.sourcePlatform || 'RSS'}
                              </Badge>
                              <span className="text-xs text-muted-foreground">
                                {article.publishDate
                                  ? format(new Date(article.publishDate), 'MMM d, yyyy')
                                  : 'Unknown date'}
                              </span>
                              {isAlreadyAdded && (
                                <Badge variant="outline" className="text-xs px-1.5 py-0 text-muted-foreground">
                                  Already added
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Your Episode */}
          <div className="article-panel w-[500px] flex-shrink-0">
            {/* Panel Header */}
            <div className="panel-header">
              <span className="panel-title">Your Episode</span>
              <motion.span
                animate={badgePop ? { scale: [1, 1.25, 1] } : { scale: 1 }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
                className={`count-badge ${existingClipCount + selectedArticles.length >= maxArticles ? 'limit-reached' : ''}`}
              >
                {existingClipCount + selectedArticles.length}/{maxArticles}
              </motion.span>
            </div>

            {/* Panel Content - Selected articles list or empty state */}
            <div className="panel-content">
              {existingClips.length === 0 && selectedArticles.length === 0 ? (
                <div
                  className="h-full min-h-[400px] flex flex-col items-center justify-center text-muted-foreground rounded-md p-4"
                  style={{ border: '2px dashed var(--pm-cyan-border-faint)' }}
                >
                  <FileText className="h-8 w-8 mb-2 opacity-50" />
                  <p className="text-sm text-center">
                    Select articles from the left to build your episode
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Existing clips - locked, not reorderable */}
                  {existingClips.map((clip, index) => (
                    <div
                      key={clip.id}
                      className="flex items-center gap-3 opacity-60"
                    >
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-muted-foreground/50 text-muted flex items-center justify-center text-xs font-medium">
                        {index + 1}
                      </div>
                      <div className="flex-1 min-w-0 bg-muted/30 border border-muted-foreground/30 rounded-md px-2 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate text-muted-foreground">
                              {clip.articleTitle}
                            </p>
                          </div>
                          <Badge variant="outline" className="text-xs px-1.5 py-0 text-muted-foreground flex-shrink-0">
                            Added
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Newly selected articles - reorderable */}
                  {selectedArticles.length > 0 && (
                    <Reorder.Group
                      axis="y"
                      values={selectedArticles}
                      onReorder={setSelectedArticles}
                      className="space-y-2"
                    >
                      <AnimatePresence initial={false}>
                        {selectedArticles.map((article, index) => (
                          <Reorder.Item
                            key={article.id}
                            value={article}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: -20 }}
                            transition={{ duration: 0.2 }}
                            className="flex items-center gap-3"
                          >
                            <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium">
                              {existingClipCount + index + 1}
                            </div>
                            <div className="flex-1 min-w-0 bg-card border rounded-md px-2 py-3 cursor-grab active:cursor-grabbing">
                              <div className="flex items-center gap-2">
                                <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">
                                    {article.title}
                                  </p>
                                </div>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    toggleArticle(article);
                                  }}
                                  className="flex-shrink-0 p-1 hover:bg-muted rounded text-muted-foreground hover:text-foreground transition-colors"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          </Reorder.Item>
                        ))}
                      </AnimatePresence>
                    </Reorder.Group>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Celebration toast */}
        <div className={`celebration-toast ${showCelebration ? 'show' : ''}`}>
          <div className="toast-check-circle">
            <svg className="toast-check-icon" viewBox="0 0 24 24">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <div className="toast-content">
            <div className="toast-title">Perfect! All set</div>
            <div className="toast-subtitle">{maxSelectable} articles ready for your episode</div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={selectedArticles.length === 0 || submitting}
            data-testid="add-articles-submit"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Adding...
              </>
            ) : (
              <>Add to Episode</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
